import { ROUTE_CHILDREN_MAP } from '@/components/Navigation/navigationChildRoutes';
import { readOnlyMenu } from '@/utils/promoterAccess';
import { screenPath } from '@/router/screenRoutes';

const UNLISTED = 99;

export const HOME_HREF = screenPath('hub');

const byScreen = (positions) => Object.fromEntries(
  Object.entries(positions).map(([name, position]) => [screenPath(name), position]),
);

const ORDER = {
  Gestão: {
    header: 1,
    items: byScreen({
      hub: 0,
      dashboard: 1,
      promoters: 2,
      users: 3,
      'corban-screening': 4,
      'activation-campaign': 5,
    }),
  },
  FGTS: {
    header: 2,
    items: byScreen({
      'fgts-consult': 0,
      'fgts-proposal': 1,
    }),
  },
  CLT: {
    header: 3,
    items: byScreen({
      'worker-credit-consult': 0,
      'worker-credit-proposal': 1,
      'worker-credit-proposal-action': 2,
      'worker-credit-auction-leads': 3,
      'worker-credit-contract-management-companies': 4,
      'worker-credit-contract-management': 5,
    }),
  },
  'Cartão de Crédito': {
    header: 4,
    items: byScreen({
      'credit-card-simulator': 0,
      'credit-card-proposals': 1,
    }),
  },
  CGV: {
    header: 5,
    items: byScreen({
      'cgv-consult': 0,
      'cgv-proposals': 1,
    }),
  },
  Mais: { header: 999, items: {} },
};

const rank = (positions, key) => (
  Object.prototype.hasOwnProperty.call(positions ?? {}, key) ? positions[key] : UNLISTED
);

const isRoute = (route) => route !== null
  && typeof route === 'object'
  && typeof route.href === 'string'
  && typeof route.text === 'string';

function storedRoutes() {
  try {
    const routers = JSON.parse(localStorage.getItem('menu'))?.routers;

    return Array.isArray(routers) ? routers.filter(isRoute) : [];
  } catch {
    return [];
  }
}

const routeKey = (route) => [route['menu-item'], route.text, route.href, route.icon, route.tag].join('::');

function dedupe(routes) {
  const seen = new Set();

  return routes.filter((route) => {
    const key = routeKey(route);

    if (seen.has(key)) return false;

    seen.add(key);
    return true;
  });
}

const byPosition = (positions) => (a, b) => rank(positions, a.href) - rank(positions, b.href);

const withChildren = (route) => ({ ...route, children: ROUTE_CHILDREN_MAP[route.href] ?? [] });

function groupSections(items) {
  const groups = items.reduce((map, item) => {
    const title = item['menu-item'];

    if (!title) return map;

    if (map.has(title)) map.get(title).push(item);
    else map.set(title, [item]);

    return map;
  }, new Map());

  return [...groups]
    .map(([title, routes]) => ({
      title,
      items: [...routes].sort(byPosition(ORDER[title]?.items)),
    }))
    .sort((a, b) => rank(ORDER[a.title], 'header') - rank(ORDER[b.title], 'header'));
}

export function buildNavigationMenu({ consultPaths = null, routers = null } = {}) {
  const routes = dedupe(Array.isArray(routers) ? routers.filter(isRoute) : storedRoutes());
  const visible = consultPaths ? readOnlyMenu(routes, consultPaths) : routes;
  const items = visible.map(withChildren);

  return { items, sections: groupSections(items) };
}

export const normalizeSearch = (text) => text
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

export function searchNavigation(sections, query) {
  const words = normalizeSearch(query ?? '').split(/\s+/).filter(Boolean);

  if (!words.length) return null;

  return sections.flatMap(({ title, items }) => items
    .filter((item) => words.every((word) => normalizeSearch(`${item.text} ${title}`).includes(word)))
    .map((item) => ({ ...item, group: title })));
}

const isWithin = (path, prefix) => path === prefix || path.startsWith(`${prefix.replace(/\/$/, '')}/`);

export function matchesRoute(item, path) {
  return path === item.href || (item.children ?? []).some((prefix) => isWithin(path, prefix));
}

export function sectionForPath(sections, path) {
  const section = sections.find(({ items }) => items.some((item) => matchesRoute(item, path)));

  return section?.title ?? null;
}

export const toggleSection = (open, title) => (
  open.includes(title) ? open.filter((section) => section !== title) : [...open, title]
);

export const openSectionFor = (open, title) => (
  !title || open.includes(title) ? open : [...open, title]
);

export const hasGroupedNavigation = (sections) => sections.length > 1;

export const isMenuSearchShortcut = (event) => (event.ctrlKey || event.metaKey)
  && !event.altKey
  && !event.shiftKey
  && event.key?.toLowerCase() === 'k';

export const searchShortcutLabel = (platform) => (/mac/i.test(platform ?? '') ? '⌘ K' : 'Ctrl K');

export const emptySearchMessage = (query) => [
  `Nenhum resultado para “${query.trim()}”.`,
  'Busque pelo nome da tela ou do produto.',
];

export function searchAnnouncement(results, query) {
  if (!results) return '';
  if (!results.length) return emptySearchMessage(query).join(' ');

  return results.length === 1
    ? '1 resultado. Enter abre a tela.'
    : `${results.length} resultados. Enter abre o primeiro.`;
}

export function searchKeyAction({
  key, query, searchesOpen, searchesDismissed, mobile,
}) {
  if (key === 'Escape') {
    if (query) return 'clear';
    if (searchesOpen) return 'dismiss';

    return mobile ? 'pass' : 'leave';
  }

  if (key === 'Enter') return query ? 'open' : null;
  if (key !== 'ArrowDown') return null;

  return !query && searchesDismissed ? 'reopen' : 'first';
}

export function listStep({ key, index, length }) {
  if (key !== 'ArrowDown' && key !== 'ArrowUp') return null;
  if (key === 'ArrowUp' && index <= 0) return 'field';

  return Math.min(index + (key === 'ArrowDown' ? 1 : -1), length - 1);
}

const PANEL_WIDTH = 320;
const PANEL_ROOM = 428;
const PANEL_GAP = 8;

export function recentsPanelPlacement({
  column, anchor, viewportHeight, mobile,
}) {
  if (mobile) {
    return {
      left: `${column.left}px`,
      top: `${column.top}px`,
      width: `${column.width}px`,
      height: `${column.height}px`,
    };
  }

  const top = Math.max(PANEL_GAP, Math.min(anchor.top, viewportHeight - PANEL_ROOM));

  return {
    left: `${column.right}px`,
    top: `${top}px`,
    width: `${PANEL_WIDTH}px`,
    maxHeight: `${viewportHeight - top - PANEL_GAP}px`,
  };
}
